import type { MoodboardItem, MoodboardSeed } from '../db/schema/moodboard'

// The starter board seeded during onboarding: its copy and pure helpers. Shared with
// the frontend (which builds the create payload and drives the fill), so it must stay
// free of server-only imports. The server-side fill patch is in ./applyStarterFill.ts.
//
// Board layout, left to right: the website (when given), then the voice note, the
// audience note, and the invitation note. When the website has been read, the fill
// (routes/stream/onboardingFill.ts) rewrites the voice and audience notes from it and
// swaps the invitation for the site's logo and colors.

export const STARTER_GENERIC_TEXT = {
  voice: 'How should your posts sound? For example: warm and direct, a little playful, no jargon.',
  audience:
    'Who are you talking to? For example: busy café owners who want more regulars. One sentence is enough.',
  invite:
    'Show CASSI your style: drop in your logo, brand colors, photos, posts you love, or links. Drag files straight onto the board.',
} as const

export type StarterSlot = MoodboardSeed['slot']

/** The item an onboarding seed slot currently occupies, if it is still untouched. */
export function findSeeded(
  items: MoodboardItem[],
  slot: StarterSlot,
  source?: MoodboardSeed['source'],
): MoodboardItem | undefined {
  return items.find(
    (i) => i.seed?.slot === slot && (source === undefined || i.seed.source === source),
  )
}

/** True when a successfully scraped website sits on the board, so the fill has material. */
export function hasReadableWebsite(item: MoodboardItem | undefined): boolean {
  return (
    item?.type === 'url' &&
    !!item.scrapedContent &&
    !item.scrapedContent.startsWith('[Scrape failed')
  )
}

/** True once the website has been enriched with something the fill can use: readable
 *  text for the notes, or colors / logos for the invitation slot. The color and logo
 *  extraction still works when the text scrape fails. */
export function hasFillableWebsite(item: MoodboardItem | undefined): boolean {
  if (item?.type !== 'url' || item.scrapedContent == null) return false
  return (
    hasReadableWebsite(item) ||
    (item.extractedColors?.length ?? 0) > 0 ||
    (item.extractedLogos?.length ?? 0) > 0
  )
}
